// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { portfolioApi } from '@shared/data-access/api/ai/portfolio-api';
import {
  type PortfolioRebalanceGeneral,
  type RebalanceTrade,
  type RebalanceReportingTargetResp,
  type RebalanceReportingTaxesResp,
} from '@shared/data-access/interfaces/api/ai/portfolio-rebalance';
import { type PositionsAnalysis } from '@shared/data-access/interfaces/redux/portfolio-state';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  resetRebalanceConfig,
  selectRebalanceConfig,
} from '@advisor-app/store/slices/parameters';
import {
  selectCurrentPortfolio,
  selectTargetPortfolio,
  portfolioPositionsSlice,
} from '@shared/data-access/slices/portfolio-positions';
import { getCurrentDate } from '@shared/ui-kit/utils/get-current-date';
import { getAssetsInfo } from '@shared/data-access/transformers/assets/get-assets-info';
import { extractAssetTickers, mapTradeToPosition } from '../utils';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

export const useRunRebalance = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [target, setTarget] = useState<RebalanceReportingTargetResp>({
    mean_absolute_error: 0,
    asset_error: [],
  });
  const [tax, setTax] = useState<RebalanceReportingTaxesResp>({
    capital_gains: {
      short_term: 0,
      long_term: 0,
      total: 0,
    },
    taxes: {
      short_term: 0,
      long_term: 0,
      total: 0,
    },
    trades_detail: [],
  });

  const dispatch = useAppDispatch();
  const currentPortfolio = useAppSelector(selectCurrentPortfolio);
  const targetPortfolio = useAppSelector(selectTargetPortfolio);
  const {
    amount,
    withdrawalOpt,
    type,
    enableTransfers = false,
    inputType = 'amount',
  } = useAppSelector(selectRebalanceConfig);

  const [triggerGeneral] = portfolioApi.useLazyRebalanceGeneralQuery();
  const [triggerRepTarget] = portfolioApi.useLazyRebalanceReportingTargetQuery();
  const [triggerRepTaxes] = portfolioApi.useLazyRebalanceReportingTaxesQuery();

  const basePayload = useMemo(
    () => ({
      holdings: currentPortfolio,
      target_strategy: {
        target_asset: targetPortfolio.map(asset => ({
          ...asset,
          weight: asset.weight / 100,
        })),
      },
      transfers: enableTransfers,
    }),
    [currentPortfolio, targetPortfolio, enableTransfers],
  );

  const runRebalance = useCallback(async () => {
    setIsLoading(true);
    /* istanbul ignore next */
    try {
      const currentDate = getCurrentDate();
      const boundaries = withdrawalOpt.withdrawalBoundaries;

      const rebalancingByType = {
        withdrawal: async (): Promise<RebalanceTrade[]> => {
          const generalPayload: PortfolioRebalanceGeneral = {
            ...basePayload,
            additional_amount: -amount, // Negative for withdrawal
            withdrawal_opt: {
              target_error: withdrawalOpt.targetError,
              withdrawal_boundaries: [amount - 1, amount * (1 + boundaries[1])],
              order_strategy: withdrawalOpt.orderStrategy,
            },
            holdings: currentPortfolio.map(holding => ({
              ...holding,
              date: currentDate,
            })),
          };

          return await triggerGeneral({
            payload: generalPayload,
          }).unwrap();
        },
        deposit: async (): Promise<RebalanceTrade[]> => {
          const generalPayload: PortfolioRebalanceGeneral = {
            ...basePayload,
            additional_amount: amount, // Positive for deposit
          };

          return await triggerGeneral({
            payload: generalPayload,
          }).unwrap();
        },
        adjustStrategy: async (): Promise<RebalanceTrade[]> => {
          const generalPayload: PortfolioRebalanceGeneral = {
            ...basePayload,
            additional_amount: amount, // Zero for strategy adjustment
            holdings: [...basePayload.holdings],
          };

          return await triggerGeneral({
            payload: generalPayload,
          }).unwrap();
        },
      };

      const rebalanceResp = await rebalancingByType[
        type as keyof typeof rebalancingByType
      ]();

      // Extract all asset tickers from trades
      const assetTickers = extractAssetTickers(rebalanceResp);
      const positionsAssets = await getAssetsInfo(assetTickers, dispatch);

      // Normalize trades to ensure all required fields are present
      const newTrades = rebalanceResp.map(trade => {
        // Ensure ticker is set based on operation type
        let ticker = trade.ticker;
        if (!ticker) {
          if (
            trade.operation === OperationType.BUY ||
            trade.operation === OperationType.TRANSFER
          ) {
            ticker = trade.to_ticker ?? '';
          } else if (trade.operation === OperationType.SELL) {
            ticker = trade.from_ticker ?? '';
          }
        }

        return {
          ...trade,
          ticker: ticker ?? '',
          date: currentDate,
        };
      });

      const newHoldings = basePayload.holdings.map(holding => ({
        ...holding,
        date: currentDate,
      }));

      const rebalanceTargetResp = await triggerRepTarget({
        payload: {
          trades: newTrades,
          holdings: newHoldings,
          target_strategy: basePayload.target_strategy,
        },
      }).unwrap();

      setTarget(rebalanceTargetResp);

      const currentYear = getCurrentDate(true);

      const rebalanceTaxesResp = await triggerRepTaxes({
        payload: {
          trades: newTrades,
          tax_config: {
            short_term_date: currentYear,
            short_term_ratio: 0.4,
            long_term_ratio: 0.2,
          },
        },
      }).unwrap();

      setTax(rebalanceTaxesResp);

      // Map trades to positions using utility function
      const updatedOptions = rebalanceResp
        .map(position => {
          const institutionData = currentPortfolio.find(
            holding =>
              holding.ticker === position.from_ticker ||
              holding.ticker === position.to_ticker ||
              holding.ticker === position.from_isin ||
              holding.ticker === position.to_isin,
          );

          return {
            ...position,
            institution_id: institutionData?.institution_id,
            institution_name: institutionData?.institution_name,
            account_id: institutionData?.account_id,
            inputType,
          };
        })
        .map(trade => mapTradeToPosition(trade, positionsAssets))
        .filter((position): position is PositionsAnalysis => position !== null);

      dispatch(
        portfolioPositionsSlice.actions.setTradesTablePortfolioPositions(
          updatedOptions,
        ),
      );
    } catch (error) {
      console.error('[runRebalance]', error);
    }
    setIsLoading(false);
  }, [
    withdrawalOpt.withdrawalBoundaries,
    withdrawalOpt.targetError,
    withdrawalOpt.orderStrategy,
    type,
    dispatch,
    basePayload,
    triggerRepTarget,
    triggerRepTaxes,
    amount,
    currentPortfolio,
    triggerGeneral,
    inputType,
  ]);

  const resetRunRebalance = () => {
    dispatch(resetRebalanceConfig());
  };

  const handleDeleteOption = async (index: number) => {
    dispatch(portfolioPositionsSlice.actions.deleteTradesTablePosition(index));
  };

  useEffect(() => {
    runRebalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleDeleteOption,
    resetRunRebalance,
    runRebalance,
    isLoading,
    target,
    tax,
    currentPortfolio,
    targetPortfolio,
  };
};
